import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EmploymentTemplateListItem } from "services/api/company/staff.service";
import { useToastStore } from "stores/toastStore/toastStore";
import { toastActions } from "stores/toastStore/toastStore.actions";
import { useCompanySubscription } from "web-apps/company/components/common/hooks/useCompanySubscription";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { getAgeFromPersonalIdentityNumber } from "web-apps/company/utils/personalIdentityNumber.utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  getAddStaffSectionCompletion,
  getVisibleAddStaffSections,
  hasCompletedAddStaffSections,
  type AddStaffSectionCompletion,
  type AddStaffSectionName,
} from "./addStaffPanel.sections.logic";
import type {
  AddStaffFieldErrors,
  AddStaffFormData,
  AddStaffFormMode,
  AddStaffOption,
  AddStaffValidatedField,
  AddStaffValidationRequirements,
} from "./addStaffPanel.contract";
import {
  applyEmploymentTemplateToAddStaffForm,
  createInitialFormData,
  isAddStaffFormStarted,
} from "./addStaffPanel.mapper";
import {
  getSalaryAmountLabel,
  getAddStaffOptions,
} from "./addStaffPanel.options.logic";
import {
  fetchEmployeeNumberRequest,
  fetchEmploymentTemplateDetailsRequest,
  fetchEmploymentTemplatesRequest,
  lookupWorkerByPhoneRequest,
  saveAddStaffFormRequest,
  type EmploymentTemplateDetails,
  type PhoneLookupState,
  type SubmitState,
} from "./addStaffPanel.request.logic";
import { canLookupTaxTable } from "../shared/staffTaxTableLookup.logic";
import { useTaxTableLookup } from "../shared/useTaxTableLookup.hook";
import {
  getAddStaffFieldStates,
  getAddStaffValidationRequirements,
} from "./addStaffPanel.validator";
import {
  toEmploymentTemplateMenuOptions,
  type EmploymentTemplateMenuOption,
} from "../shared/staffEmploymentTemplate.logic";
import {
  toEmploymentUiRules,
  withEmploymentTypeChangePrefill,
  withNormalizedEmploymentFormByRules,
  type EmploymentUiRules,
} from "../shared/staffEmploymentRules.logic";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toApiPhoneNumber } from "utils/phoneNumber.logic";

interface UseAddStaffFormLogicProps {
  /**
   * Values the form starts from instead of the empty defaults, read each time
   * the form opens. A value left undefined keeps its default. An employee
   * number given here is kept rather than fetched.
   */
  readonly initialFormData?: Partial<AddStaffFormData>;
  readonly isOpen: boolean;
  readonly mode: AddStaffFormMode;
  readonly t: TFunction;
  readonly unitTeamOptions: UnitTeamOption[];
  /** The worker a new employment is added to; unused for a new worker. */
  readonly workerId: number | null;
  readonly onSaved: () => void;
}

export interface AddStaffFormLogicResult {
  readonly areFormFieldsDisabled: boolean;
  readonly autofilledFields: ReadonlySet<keyof AddStaffFormData>;
  readonly employeeCategoryOptions: AddStaffOption[];
  readonly employmentOptions: AddStaffOption[];
  readonly employmentTemplateMenuOptions: EmploymentTemplateMenuOption[];
  readonly fieldErrors: AddStaffFieldErrors;
  readonly formData: AddStaffFormData;
  readonly isCreateDisabled: boolean;
  readonly isFormStarted: boolean;
  readonly isLoadingEmployeeNumber: boolean;
  readonly isLoadingTaxTable: boolean;
  readonly isLoadingEmploymentTemplateMenu: boolean;
  readonly isSubmitting: boolean;
  readonly mode: AddStaffFormMode;
  readonly paymentMethodOptions: AddStaffOption[];
  readonly paymentOptions: AddStaffOption[];
  readonly salaryAmountLabel: string;
  readonly salaryOptions: AddStaffOption[];
  readonly sectionCompletion: AddStaffSectionCompletion;
  readonly submitError: string;
  readonly teamOptions: AddStaffOption[];
  readonly vacationPayoutOptions: AddStaffOption[];
  readonly validationRequirements: AddStaffValidationRequirements;
  readonly visibleSections: AddStaffSectionName[];
  readonly applyEmploymentTemplateSelection: (selection: {
    salaryId: string;
    templateId: string;
  }) => void;
  readonly fetchEmployeeNumber: () => void;
  readonly onPersonalIdentityNumberBlur: () => void;
  readonly onPhoneNumberBlur: () => void;
  readonly onPhoneNumberChange: (phoneNumber: string) => void;
  readonly onSubmitClick: () => void;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
}

const AUTOFILL_HIGHLIGHT_MS = 1200;

const PHONE_LOOKUP_DEBOUNCE_MS = 400;

const TAX_TABLE_LOOKUP_DEBOUNCE_MS = 400;

/**
 * Applies the employment rules to the form, returning the same object when
 * nothing changes so an untouched form stays equal to its initial state.
 */
const withNormalizedFormDataByRules = (
  formData: AddStaffFormData,
  employmentRules: EmploymentUiRules,
): AddStaffFormData => {
  const current = {
    salaryType: formData.salaryType ?? "",
    employeeCategory: formData.employeeCategory,
    paymentType: formData.paymentType,
  };
  const normalized = withNormalizedEmploymentFormByRules(
    current,
    employmentRules,
  );
  return normalized.salaryType === current.salaryType &&
    normalized.employeeCategory === current.employeeCategory &&
    normalized.paymentType === current.paymentType
    ? formData
    : { ...formData, ...normalized };
};

const withInitialValues = (
  defaults: AddStaffFormData,
  initialValues: Partial<AddStaffFormData> | undefined,
): AddStaffFormData => {
  const givenValues = Object.fromEntries(
    Object.entries(initialValues ?? {}).filter(
      ([, value]) => value !== undefined,
    ),
  ) as Partial<AddStaffFormData>;
  return { ...defaults, ...givenValues };
};

const toChangedFormFields = (
  previousFormData: AddStaffFormData,
  nextFormData: AddStaffFormData,
): Set<keyof AddStaffFormData> => {
  const formFields = Object.keys(nextFormData) as (keyof AddStaffFormData)[];
  return new Set(
    formFields.filter(
      (field) => previousFormData[field] !== nextFormData[field],
    ),
  );
};

export const useAddStaffFormLogic = (
  props: UseAddStaffFormLogicProps,
): AddStaffFormLogicResult => {
  const isOpen = props.isOpen;
  const mode = props.mode;
  const workerId = props.workerId;
  const onSaved = props.onSaved;
  const translate = props.t;
  const unitTeamOptions = props.unitTeamOptions;
  const [companyState] = useCompanyStore();
  const [, , toastDispatch] = useToastStore();
  const companyId = companyState.company?.id;
  const initialFormDataPropRef = useRef(props.initialFormData);
  initialFormDataPropRef.current = props.initialFormData;
  const initialFormDataRef = useRef<AddStaffFormData | null>(null);
  const [formData, setFormData] = useState<AddStaffFormData>(() => {
    const initialFormData = createInitialFormData();
    initialFormDataRef.current = initialFormData;
    return initialFormData;
  });
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  // Destructured rather than held as one object: the two callbacks are stable,
  // so effects can depend on them without rescheduling every time the in-flight
  // flag flips.
  const {
    cancel: cancelTaxTableRequest,
    isLoading: isLoadingTaxTable,
    lookup: lookupTaxTable,
  } = useTaxTableLookup();
  const [autofilledFields, setAutofilledFields] = useState<
    ReadonlySet<keyof AddStaffFormData>
  >(new Set());
  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [employmentTemplateDetails, setEmploymentTemplateDetails] =
    useState<EmploymentTemplateDetails>({});
  const [
    isLoadingEmploymentTemplateDetails,
    setIsLoadingEmploymentTemplateDetails,
  ] = useState(false);
  const [phoneLookupState, setPhoneLookupState] = useState<PhoneLookupState>({
    status: "idle",
  });
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: "idle",
  });
  const [submitError, setSubmitError] = useState("");
  const isMountedRef = useRef(true);
  const employeeNumberRequestIdRef = useRef(0);
  const submitRequestIdRef = useRef(0);
  const phoneLookupRequestIdRef = useRef(0);
  const phoneLookupTimeoutRef = useRef<number>();
  const taxTableTimeoutRef = useRef<number>();
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailsRequestIdRef = useRef(0);
  const companyAddressRef = useRef(companyState.company?.address);
  companyAddressRef.current = companyState.company?.address;
  const companySettingsRef = useRef(
    companyState.company?.companySettings ?? [],
  );
  companySettingsRef.current = companyState.company?.companySettings ?? [];
  const subscription = useCompanySubscription({
    companyId,
    isEnabled: isOpen,
  });
  const subscriptionRef = useRef<string | undefined>(undefined);
  subscriptionRef.current = subscription;

  const cancelPendingTaxTableLookup = useCallback((): void => {
    if (taxTableTimeoutRef.current == null) return;
    window.clearTimeout(taxTableTimeoutRef.current);
    taxTableTimeoutRef.current = undefined;
  }, []);

  const cancelPendingPhoneLookup = useCallback((): void => {
    if (phoneLookupTimeoutRef.current == null) return;
    window.clearTimeout(phoneLookupTimeoutRef.current);
    phoneLookupTimeoutRef.current = undefined;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelPendingPhoneLookup();
    };
  }, [cancelPendingPhoneLookup]);

  /**
   * Flags fields the user did not type into themselves, so the form can point
   * out what a template or a phone lookup changed.
   */
  const markAutofilledFields = useCallback(
    (fields: Set<keyof AddStaffFormData>): void => {
      if (fields.size === 0) return;
      setAutofilledFields(fields);
    },
    [],
  );

  useEffect(() => {
    if (autofilledFields.size === 0) return;
    const timeout = window.setTimeout(
      () => setAutofilledFields(new Set()),
      AUTOFILL_HIGHLIGHT_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [autofilledFields]);

  const setField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      setFormData((previousFormData) =>
        withEmploymentTypeChangePrefill({
          changedField: field,
          changedValue: value,
          companyAddress: companyAddressRef.current,
          form: { ...previousFormData, [field]: value },
        }),
      );
      setSubmitError("");
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    if (!companyId) return;
    const requestId = employeeNumberRequestIdRef.current + 1;
    employeeNumberRequestIdRef.current = requestId;
    setIsLoadingEmployeeNumber(true);
    void fetchEmployeeNumberRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employeeNumberRequestIdRef.current,
      isMountedRef,
      setField,
      setIsLoadingEmployeeNumber,
    });
  }, [companyId, setField]);

  const startTaxTableLookup = useCallback(
    (personalIdentityNumber: string): void => {
      lookupTaxTable({
        companyId,
        personalIdentityNumber,
        onFound: (taxTable) => setField("taxTable", taxTable),
      });
    },
    [companyId, lookupTaxTable, setField],
  );

  useEffect(() => {
    if (!isOpen) return;
    // The tax table is only part of the form for a new worker.
    if (mode !== "newWorker") return;
    if (!canLookupTaxTable(formData.personalIdentityNumber)) return;

    const personalIdentityNumber = formData.personalIdentityNumber;
    taxTableTimeoutRef.current = window.setTimeout(() => {
      taxTableTimeoutRef.current = undefined;
      startTaxTableLookup(personalIdentityNumber);
    }, TAX_TABLE_LOOKUP_DEBOUNCE_MS);

    // A lookup belongs to the personal number, company and open panel it was
    // started for; once any of them changes its answer must not land.
    return () => {
      cancelPendingTaxTableLookup();
      cancelTaxTableRequest();
    };
  }, [
    cancelPendingTaxTableLookup,
    cancelTaxTableRequest,
    formData.personalIdentityNumber,
    isOpen,
    mode,
    startTaxTableLookup,
  ]);

  const onPersonalIdentityNumberBlur = useCallback((): void => {
    if (taxTableTimeoutRef.current == null) return;
    cancelPendingTaxTableLookup();
    startTaxTableLookup(formData.personalIdentityNumber);
  }, [
    cancelPendingTaxTableLookup,
    formData.personalIdentityNumber,
    startTaxTableLookup,
  ]);

  const fetchEmploymentTemplates = useCallback((): void => {
    if (!companyId) return;
    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplates(true);
    void fetchEmploymentTemplatesRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employmentTemplatesRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplates,
      setIsLoadingEmploymentTemplates,
    });
  }, [companyId]);

  const lookupPhoneNumber = useCallback(
    (phoneNumber: string): void => {
      if (!companyId) return;
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setPhoneLookupState({ status: "checking" });
      const requestId = phoneLookupRequestIdRef.current + 1;
      phoneLookupRequestIdRef.current = requestId;
      void lookupWorkerByPhoneRequest({
        companyId,
        phoneNumber,
        requestId,
        getCurrentRequestId: () => phoneLookupRequestIdRef.current,
        isMountedRef,
        markAutofilledFields,
        setFormData,
        setPhoneLookupState,
        translate,
      });
    },
    [companyId, markAutofilledFields, translate],
  );

  const onPhoneNumberChange = useCallback(
    (phoneNumber: string): void => {
      phoneLookupRequestIdRef.current += 1;
      cancelPendingPhoneLookup();
      setPhoneLookupState({ status: "idle" });
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setField("phoneNumber", phoneNumber);

      if (!isValidPhoneNumber(phoneNumber)) return;
      phoneLookupTimeoutRef.current = window.setTimeout(() => {
        phoneLookupTimeoutRef.current = undefined;
        lookupPhoneNumber(phoneNumber);
      }, PHONE_LOOKUP_DEBOUNCE_MS);
    },
    [cancelPendingPhoneLookup, lookupPhoneNumber, setField],
  );

  const onPhoneNumberBlur = useCallback((): void => {
    if (!isValidPhoneNumber(formData.phoneNumber)) {
      cancelPendingPhoneLookup();
      if (formData.phoneNumber.trim().length === 0) {
        setPhoneLookupState({ status: "idle" });
        return;
      }
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    // Leaving the field says the number is finished, so run the lookup now
    // rather than making the user wait out the rest of the debounce.
    if (phoneLookupTimeoutRef.current != null) {
      cancelPendingPhoneLookup();
      lookupPhoneNumber(formData.phoneNumber);
      return;
    }
    if (phoneLookupState.status === "invalid") {
      setPhoneLookupState({ status: "idle" });
    }
  }, [
    cancelPendingPhoneLookup,
    formData.phoneNumber,
    lookupPhoneNumber,
    phoneLookupState.status,
    translate,
  ]);

  const loadEmploymentTemplateDetails = useCallback((): void => {
    if (!companyId) return;
    if (employmentTemplates.length === 0) return;
    const requestId = employmentTemplateDetailsRequestIdRef.current + 1;
    employmentTemplateDetailsRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplateDetails(true);
    void fetchEmploymentTemplateDetailsRequest({
      companyId,
      employmentTemplateIds: employmentTemplates.map((template) => template.id),
      requestId,
      getCurrentRequestId: () => employmentTemplateDetailsRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplateDetails,
      setIsLoadingEmploymentTemplateDetails,
    });
  }, [companyId, employmentTemplates]);

  const applyEmploymentTemplateSelection = useCallback(
    (selection: { salaryId: string; templateId: string }): void => {
      const template = employmentTemplateDetails[selection.templateId];
      if (template == null) return;

      const selectedSalary =
        selection.salaryId.length > 0
          ? template.salaries?.find(
              (salary) => String(salary.id) === selection.salaryId,
            )
          : undefined;
      const templateFormData = applyEmploymentTemplateToAddStaffForm(
        formData,
        template,
      );
      const nextFormData =
        selectedSalary == null
          ? templateFormData
          : {
              ...templateFormData,
              salaryAmount: String(selectedSalary.value),
            };

      setFormData(nextFormData);
      setSubmitError("");
      markAutofilledFields(toChangedFormFields(formData, nextFormData));
    },
    [employmentTemplateDetails, formData, markAutofilledFields],
  );

  useEffect(() => {
    submitRequestIdRef.current += 1;
    employeeNumberRequestIdRef.current += 1;
    phoneLookupRequestIdRef.current += 1;
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailsRequestIdRef.current += 1;
    cancelTaxTableRequest();
    cancelPendingTaxTableLookup();
    cancelPendingPhoneLookup();
    if (!isOpen) return;

    const initialFormData = withNormalizedFormDataByRules(
      withInitialValues(
        createInitialFormData(),
        initialFormDataPropRef.current,
      ),
      toEmploymentUiRules({
        companySettings: companySettingsRef.current,
        employmentType: initialFormDataPropRef.current?.employmentType ?? "",
        salaryType: initialFormDataPropRef.current?.salaryType ?? "",
        subscription: subscriptionRef.current,
      }),
    );
    initialFormDataRef.current = initialFormData;
    setFormData(initialFormData);
    setPhoneLookupState({ status: "idle" });
    setSubmitState({ status: "idle" });
    setSubmitError("");
    setEmploymentTemplates([]);
    setEmploymentTemplateDetails({});
    setIsLoadingEmploymentTemplateDetails(false);
    setAutofilledFields(new Set());
    if (initialFormData.employeeNumber.trim().length === 0) {
      fetchEmployeeNumber();
    }
    fetchEmploymentTemplates();
  }, [
    cancelPendingPhoneLookup,
    cancelPendingTaxTableLookup,
    cancelTaxTableRequest,
    fetchEmployeeNumber,
    fetchEmploymentTemplates,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    loadEmploymentTemplateDetails();
  }, [isOpen, loadEmploymentTemplateDetails]);

  const employmentRules = useMemo(() => {
    return toEmploymentUiRules({
      companySettings: companyState.company?.companySettings ?? [],
      employmentType: formData.employmentType ?? "",
      salaryType: formData.salaryType ?? "",
      subscription,
    });
  }, [
    companyState.company?.companySettings,
    formData.employmentType,
    formData.salaryType,
    subscription,
  ]);

  useEffect(() => {
    setFormData((previousFormData) =>
      withNormalizedFormDataByRules(previousFormData, employmentRules),
    );
  }, [employmentRules]);

  const options = useMemo(() => {
    return getAddStaffOptions({
      employmentRules,
      translate,
      unitTeamOptions,
    });
  }, [employmentRules, translate, unitTeamOptions]);
  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(formData.personalIdentityNumber);
  }, [formData.personalIdentityNumber]);
  const employmentTemplateMenuOptions = useMemo(() => {
    return toEmploymentTemplateMenuOptions({
      templateDetails: employmentTemplateDetails,
      templates: employmentTemplates,
      workerAge,
    });
  }, [employmentTemplateDetails, employmentTemplates, workerAge]);
  const validationRequirements = useMemo(() => {
    return getAddStaffValidationRequirements(formData, mode);
  }, [formData, mode]);
  // A new employment belongs to a worker that already exists, so there is no
  // phone number to check.
  const hasCheckedPhoneNumber =
    mode === "newEmployment" || phoneLookupState.status === "verified";
  const fieldStates = useMemo(() => {
    return getAddStaffFieldStates({
      canSelectSimflexityEmployee: employmentRules.canSelectSimflexityEmployee,
      canSelectSimflexityPay: employmentRules.canSelectSimflexityPay,
      formData,
      hasVerifiedPhoneNumber: hasCheckedPhoneNumber,
      mode,
      requirements: validationRequirements,
    });
  }, [
    employmentRules.canSelectSimflexityEmployee,
    employmentRules.canSelectSimflexityPay,
    formData,
    hasCheckedPhoneNumber,
    mode,
    validationRequirements,
  ]);
  const salaryAmountLabel = useMemo(() => {
    return getSalaryAmountLabel({
      salaryType: formData.salaryType,
      translate,
    });
  }, [formData.salaryType, translate]);
  const isSubmitting = submitState.status === "submitting";
  const phoneError =
    phoneLookupState.status === "invalid" ||
    phoneLookupState.status === "alreadyExists"
      ? phoneLookupState.message
      : "";
  const fieldErrors = useMemo<AddStaffFieldErrors>(() => {
    const toFieldError = (field: AddStaffValidatedField): string => {
      const { errorTranslationKey } = fieldStates[field];
      return errorTranslationKey == null ? "" : translate(errorTranslationKey);
    };

    return {
      advanceVacationDays: toFieldError("advanceVacationDays"),
      agreement: toFieldError("agreement"),
      email: toFieldError("email"),
      employeeCategory: toFieldError("employeeCategory"),
      employeeNumber: toFieldError("employeeNumber"),
      employmentType: toFieldError("employmentType"),
      endDate: toFieldError("endDate"),
      firstName: toFieldError("firstName"),
      lastName: toFieldError("lastName"),
      maxCareOfChildDays: toFieldError("maxCareOfChildDays"),
      maxParentalLeaveDays: toFieldError("maxParentalLeaveDays"),
      maxSickLeaveDays: toFieldError("maxSickLeaveDays"),
      noticeDays: toFieldError("noticeDays"),
      paymentMethodValue: toFieldError("paymentMethodValue"),
      paymentType: toFieldError("paymentType"),
      personalIdentityNumber: toFieldError("personalIdentityNumber"),
      phoneNumber: phoneError,
      role: toFieldError("role"),
      salaryAmount: toFieldError("salaryAmount"),
      salaryType: toFieldError("salaryType"),
      startDate: toFieldError("startDate"),
      taxTable: toFieldError("taxTable"),
      teamIds: toFieldError("teamIds"),
      vacationDays: toFieldError("vacationDays"),
      workPercentage: toFieldError("workPercentage"),
      workplace: toFieldError("workplace"),
    };
  }, [fieldStates, phoneError, translate]);
  const areFormFieldsDisabled =
    mode === "newWorker" &&
    (formData.phoneNumber.trim().length === 0 || !hasCheckedPhoneNumber);
  const visibleSections = getVisibleAddStaffSections({
    employmentType: formData.employmentType,
    mode,
    salaryType: formData.salaryType,
  });
  const sectionCompletion = getAddStaffSectionCompletion(fieldStates);
  const hasCompletedEverySection = hasCompletedAddStaffSections({
    sectionCompletion,
    visibleSections,
  });
  const isCreateDisabled = isSubmitting || !hasCompletedEverySection;
  const isFormStarted = useMemo(() => {
    return (
      initialFormDataRef.current != null &&
      isAddStaffFormStarted(formData, initialFormDataRef.current)
    );
  }, [formData]);

  const onSubmitClick = useCallback((): void => {
    if (!companyId || isSubmitting) return;
    if (!hasCompletedEverySection) return;
    if (
      mode === "newWorker" &&
      !toApiPhoneNumber(formData.phoneNumber).succeeded
    ) {
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    setSubmitError("");
    setSubmitState({ status: "submitting" });
    const requestId = submitRequestIdRef.current + 1;
    submitRequestIdRef.current = requestId;
    void saveAddStaffFormRequest({
      companyId,
      formData,
      mode,
      requestId,
      translate,
      workerId,
      getCurrentRequestId: () => submitRequestIdRef.current,
      isMountedRef,
      onSaved: () => {
        toastActions(toastDispatch).showToast({
          variant: "success",
          message: translate("Staff.AddStaffPanelEmploymentCreated"),
        });
        onSaved();
      },
      setSubmitError,
      setSubmitState,
    });
  }, [
    hasCompletedEverySection,
    companyId,
    formData,
    isSubmitting,
    mode,
    onSaved,
    toastDispatch,
    translate,
    workerId,
  ]);

  return {
    areFormFieldsDisabled,
    autofilledFields,
    employeeCategoryOptions: options.employeeCategoryOptions,
    employmentOptions: options.employmentOptions,
    employmentTemplateMenuOptions,
    fieldErrors,
    formData,
    isCreateDisabled,
    isFormStarted,
    isLoadingEmployeeNumber,
    isLoadingTaxTable,
    isLoadingEmploymentTemplateMenu:
      isLoadingEmploymentTemplates || isLoadingEmploymentTemplateDetails,
    isSubmitting,
    mode,
    paymentMethodOptions: options.paymentMethodOptions,
    paymentOptions: options.paymentOptions,
    salaryAmountLabel,
    salaryOptions: options.salaryOptions,
    sectionCompletion,
    submitError,
    teamOptions: options.teamOptions,
    vacationPayoutOptions: options.vacationPayoutOptions,
    validationRequirements,
    visibleSections,
    applyEmploymentTemplateSelection,
    fetchEmployeeNumber,
    onPersonalIdentityNumberBlur,
    onPhoneNumberBlur,
    onPhoneNumberChange,
    onSubmitClick,
    setField,
  };
};
